import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@/lib/api/useApiClient';
import { useAuth } from '@/lib/auth/useAuth';
import type { ReleaseListFilters, ReleaseListResponse } from './types';

const releaseKeys = {
  all: ['releases'] as const,
  list: (filters: ReleaseListFilters) => [...releaseKeys.all, 'list', filters] as const,
  byRepo: (repoName: string, filters: ReleaseListFilters) =>
    [...releaseKeys.all, repoName, filters] as const,
};

function buildQuery(filters: ReleaseListFilters): Record<string, string | number | undefined> {
  return { limit: filters.limit, offset: filters.offset };
}

export function useReleases(filters: ReleaseListFilters) {
  const api = useApiClient();
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: releaseKeys.list(filters),
    enabled: Boolean(accessToken),
    queryFn: () => api.request<ReleaseListResponse>('/releases', { query: buildQuery(filters) }),
  });
}

export function useReleasesForRepo(repoName: string | undefined, filters: ReleaseListFilters) {
  const api = useApiClient();
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: releaseKeys.byRepo(repoName ?? '', filters),
    enabled: Boolean(repoName) && Boolean(accessToken),
    queryFn: () =>
      api.request<ReleaseListResponse>(`/releases/${encodeURIComponent(repoName!)}`, {
        query: buildQuery(filters),
      }),
  });
}

// Build the same-origin URL the asset-download fetch should hit. The repo
// path contains '/', so each path segment is URL-encoded independently.
export function buildAssetDownloadUrl(repoName: string, tag: string, assetName: string): string {
  const baseUrl = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');
  return `${baseUrl}/releases/${encodeURIComponent(repoName)}/${encodeURIComponent(
    tag,
  )}/download/${encodeURIComponent(assetName)}`;
}

// Fetch the asset bytes (with the bearer token) and save them to disk via a
// blob URL + invisible anchor click. Buffers the whole response in memory —
// fine for release-asset sizes (tens of MB), but don't repurpose this for
// uploads/syslogs which can be multi-GB.
//
// We can't use `window.location.href = url` here because our auth is a JWT
// bearer token in sessionStorage, not a cookie — a plain navigation wouldn't
// carry credentials and the backend would 401.
export async function downloadAsset(
  url: string,
  filename: string,
  accessToken: string | undefined,
): Promise<void> {
  const res = await fetch(url, {
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  if (!res.ok) {
    throw new Error(`Download failed (HTTP ${res.status})`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    // The server's Content-Disposition takes precedence, but supplying the
    // filename hint here is the safer fallback.
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    // Defer revoke so the browser has time to start the download dialog
    // before we drop the underlying Blob.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 200);
  }
}
